import { useParams, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useClient } from '@/hooks/useClients';
import { ClientInfoSection } from './components/ClientInfoSection';
import { BillingAddressesSection } from './components/BillingAddressesSection';
import { ManagersSection } from './components/ManagersSection';
import { LocationsSection } from './components/LocationsSection';
import { JobsSection } from './components/JobsSection';
import { Button } from '@/components/ui/button';
import { formatClientName } from '@jobos/shared';


export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: client, isLoading, error } = useClient(id);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">
          Error loading client: {error?.message || 'Client not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <Link
                to="/clients"
                className="text-sm text-blue-600 hover:text-blue-800 mb-2 inline-block"
              >
                ← Back to Clients
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">
                {formatClientName(client.name, client.firstName, client.lastName)}
              </h1>
            </div>
            <div className="flex gap-2">
              <Link to={`/clients/${id}/edit`}>
                <Button variant="outline">Edit Client</Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Client Information */}
        <ClientInfoSection client={client} />

        {/* Billing Addresses */}
        <BillingAddressesSection clientId={id!} managers={client.managers || []} />

        {/* Managers */}
        <ManagersSection clientId={id!} managers={client.managers || []} />

        {/* Service Addresses */}
        <LocationsSection clientId={id!} locations={client.locations || []} />

        {/* Jobs */}
        <JobsSection clientId={id!} jobs={client.jobs || []} />
      </div>
  );
}
